import { HttpClient } from '@/data/protocols/http'
import { Category } from '@/domain/models/content/category'
import { ContentFormat } from '@/domain/models/content/content-format'
import { Subtype } from '@/domain/models/content/subtype'
import { ContentSummaryModel } from '@/domain/models/content/user-content-summary-model'
import { EnrollmentStatus } from '@/domain/models/enrollment/status'
import { GenericResponse } from '@/infra/http'
import { createLocalTimezoneDateFromString } from '@/main/helpers'
import { QueryClient } from 'react-query'
import { BaseRemote } from '../base-remote'

export class RemoteAccountEnrollments extends BaseRemote {
  constructor(
    protected readonly queryClient: QueryClient,
    protected readonly url: string,
    protected readonly httpClient: HttpClient<GenericResponse<RemoteAccountEnrollments.Model>>
  ) {
    super()
  }

  async loader(): Promise<RemoteAccountEnrollments.Model> {
    return this.queryClient.fetchQuery({
      queryKey: RemoteAccountEnrollments.Key,
      queryFn: () => this.get(),
      staleTime: 1000
    })
  }

  async get(): Promise<GenericResponse<RemoteAccountEnrollments.Model>> {
    const response = await this.httpClient.request({
      url: this.url,
      method: 'get'
    })

    return this.onSuccess(response, RemoteAccountEnrollments.parseResponse)
  }

  private static enrichEnrollmentResponse(
    enrollment: RemoteAccountEnrollments.Enrollment
  ): RemoteAccountEnrollments.Enrollment {
    return {
      ...enrollment,
      category: Category.getByName(String(enrollment.category)),
      format: ContentFormat.getByName(String(enrollment.format)),
      startAt: enrollment.startAt
        ? createLocalTimezoneDateFromString(String(enrollment.startAt))
        : null,
      status: EnrollmentStatus.getByName(String(enrollment.status)),
      subtype: enrollment.subTypeId != null ? Subtype.getById(Number(enrollment.subTypeId)) : null
    }
  }

  public static parseResponse(
    res: GenericResponse<RemoteAccountEnrollments.Model>
  ): GenericResponse<RemoteAccountEnrollments.Model> {
    const data = res.data!
    const { canceled, inProgress, completed } = data
    return {
      ...res,
      data: {
        canceled: canceled.map(RemoteAccountEnrollments.enrichEnrollmentResponse),
        inProgress: inProgress.map(RemoteAccountEnrollments.enrichEnrollmentResponse),
        completed: completed.map(RemoteAccountEnrollments.enrichEnrollmentResponse)
      }
    }
  }
}

export namespace RemoteAccountEnrollments {
  export type Enrollment = {
    subTypeId: number | null
    status: EnrollmentStatus.Enum | null
    institutionUserPlatformUrl: string | null
  } & ContentSummaryModel
  export type Model = {
    canceled: Enrollment[]
    completed: Enrollment[]
    inProgress: Enrollment[]
  }

  export const Key = 'user-enrollments'
}
